/**
 * Ligação de UI para o checklist de documentos obrigatórios.
 *
 * O **catálogo** (quais documentos cada produto exige) vive no **backend** —
 * `GET /v1/oportunidades/{id}/documentos-obrigatorios`, a mesma regra que bloqueia o
 * envio da reserva. Antes ele era duplicado aqui, o que significava duas fontes de
 * verdade para a mesma regra: a tela podia dizer "completo" e o backend recusar.
 *
 * O que sobra deste arquivo é presentational: casar cada item do checklist com o
 * objeto `Documento` já carregado, para preview/download.
 */
import { Documento, DocumentoTipo } from '@/types/documento'
import { CategoriaDocumento } from '@/types/estruturacao'
import type { StatusItemChecklistAtual } from '@/types/checklist'

export const TIPO_ENTIDADE_OPORTUNIDADE = 'OPORTUNIDADE'

/**
 * Anexos de pendência. O módulo de documentos é genérico (`tipoEntidade` +
 * `entidadeReferencia`), então o anexo de uma pendência é só um documento
 * apontando para o ID dela — sem mudança no backend.
 */
export const TIPO_ENTIDADE_PENDENCIA = 'PENDENCIA'

/** Item do checklist como o backend devolve. */
export interface DocumentoObrigatorio {
  label: string
  tipo: string
  presente: boolean
  /**
   * Agrupamento na estruturação (V40). OPCIONAL aqui de propósito: o backend sempre
   * manda, mas dublês de teste e caminhos legados anteriores à V40 constroem o item sem
   * ela. Quem edita o checklist trata ausência como CADASTRAL — o MESMO default do
   * backend, para que a tela não invente um grupo diferente do que seria persistido.
   */
  categoria?: CategoriaDocumento
  /**
   * Identidade do item dentro do produto (V45). E ela que o upload devolve para dizer QUAL
   * exigencia o arquivo satisfaz; nula nos itens legados por nome, que nao tem cadastro.
   */
  chave?: string | null
  /** Falso = desejavel, mas nao bloqueia o envio nem entra no percentual (V45). */
  obrigatorio?: boolean
  /** Documento da captura inicial — o bloco de OCR da estruturacao (V45). */
  basico?: boolean
  /**
   * P3: status do item calculado no backend (FALTANDO · QUARENTENA · RECEBIDO · VALIDADO ·
   * RECUSADO · VENCIDO). `presente` = RECEBIDO ou VALIDADO. Ausente em backend anterior à P3.
   */
  status?: StatusItemChecklistAtual | null
}

/** Item do checklist já vinculado ao arquivo correspondente (quando houver). */
export interface StatusDocObrigatorio extends DocumentoObrigatorio {
  documento?: Documento
}

/**
 * Vincula cada item do checklist ao `Documento` correspondente entre os já carregados.
 *
 * Primeiro pela `exigenciaChave` gravada no upload (V8/V45) — com dois itens do mesmo tipo
 * é ela que diz a qual item o arquivo pertence; antes o vínculo só pelo tipo podia mostrar
 * (e, no VC-16, trocar/excluir) o arquivo do item errado. Só o documento SEM chave (legado)
 * cai no casamento pelo tipo, e apenas para item que ainda não recebeu arquivo pela chave.
 *
 * Um documento só é usado uma vez: dois itens não apontam para o mesmo arquivo. A
 * **presença** continua vindo do backend — este vínculo é só para exibir o arquivo; se a
 * projeção do backend ainda não enxergou um upload recente, o item aparece como pendente,
 * que é o mesmo que o envio vai cobrar.
 */
export function vincularDocumentos(
  checklist: DocumentoObrigatorio[],
  documentos: Documento[],
): StatusDocObrigatorio[] {
  const usados = new Set<string>()
  // 1ª passada: pela chave da exigência.
  const porChave = checklist.map((item) => {
    if (!item.chave) return undefined
    const doc = documentos.find((d) => d.exigenciaChave === item.chave && !usados.has(d.id))
    if (doc) usados.add(doc.id)
    return doc
  })
  // 2ª passada: legado (documento sem chave) pelo tipo, só para quem ficou sem arquivo.
  return checklist.map((item, i) => {
    let doc = porChave[i]
    if (!doc) {
      doc = documentos.find(
        (d) => !d.exigenciaChave && d.tipo === (item.tipo as Documento['tipo']) && !usados.has(d.id),
      )
      if (doc) usados.add(doc.id)
    }
    return { ...item, documento: doc }
  })
}

/** Os valores de `entity/TipoDocumento` — o único vocabulário que o upload aceita em `tipo`. */
export const TIPOS_DOCUMENTO: DocumentoTipo[] = [
  'RG', 'CPF', 'CNPJ', 'CONTRATO', 'BALANCO_PATRIMONIAL', 'DRE', 'EXTRATO_BANCARIO', 'NOTA_FISCAL',
  'RECIBO', 'PROPOSTA', 'RELATORIO', 'COMPROVANTE_RENDA', 'COMPROVANTE_ENDERECO', 'EXTRATO_CONTA',
  'PLANILHA', 'APRESENTACAO', 'MANUAL', 'FATURAMENTO', 'SPED_CONTABIL', 'OUTRO',
]

/** "Balanço patrimonial" → "BALANCO_PATRIMONIAL": sem acento, maiúsculo, separadores viram `_`. */
export const normalizarTipoDocumento = (texto: string): string =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

/**
 * Que `tipo` (e qual exigência do checklist) o anexo de uma pendência deve levar no upload.
 *
 * O documento pedido na pendência é texto livre do operador ("Balanço patrimonial",
 * "Contrato social atualizado"); o upload só aceita o enum `TipoDocumento`. Mandar o texto
 * cru dava 400 de validação e a pendência nunca era respondida (11/09). A resolução vai do
 * mais preciso ao mais genérico: item do checklist da reserva com a mesma chave/tipo/rótulo
 * (traz também a `chave` da exigência) → nome do enum → prefixo único do enum
 * ("BALANCO" → BALANCO_PATRIMONIAL) → `OUTRO`. Nunca inventa: em dúvida, é OUTRO.
 */
export const tipoDocumentoParaUpload = (
  documentoSolicitado: string | null | undefined,
  checklist?: DocumentoObrigatorio[] | null
): { tipo: DocumentoTipo; exigenciaChave: string | null } => {
  const texto = (documentoSolicitado ?? '').trim()
  if (!texto) return { tipo: 'OUTRO', exigenciaChave: null }
  const chaveNormalizada = normalizarTipoDocumento(texto)

  const item = (checklist ?? []).find(
    (i) =>
      (i.chave && i.chave === texto) ||
      i.tipo === texto ||
      normalizarTipoDocumento(i.label) === chaveNormalizada ||
      normalizarTipoDocumento(i.tipo) === chaveNormalizada
  )
  if (item) {
    const tipoItem = TIPOS_DOCUMENTO.includes(item.tipo as DocumentoTipo) ? (item.tipo as DocumentoTipo) : 'OUTRO'
    return { tipo: tipoItem, exigenciaChave: item.chave ?? null }
  }

  if (TIPOS_DOCUMENTO.includes(chaveNormalizada as DocumentoTipo)) {
    return { tipo: chaveNormalizada as DocumentoTipo, exigenciaChave: null }
  }
  const porPrefixo = TIPOS_DOCUMENTO.filter((t) => t !== 'OUTRO' && t.startsWith(chaveNormalizada))
  if (porPrefixo.length === 1) return { tipo: porPrefixo[0], exigenciaChave: null }
  return { tipo: 'OUTRO', exigenciaChave: null }
}
